import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { FactTile, Ring } from '../ui/bits.tsx';
import { Icon } from '../ui/Icon.tsx';
import { asset } from '../engine/assets.ts';
import { haptic, hapticDone, reducedMotion } from '../engine/haptics.ts';
import { CEL, CORAL_DEEP, CREAM, GRAD, INK, LINE, MUTED, ORANGE, PAPER, PROGRESS_FILL, SUN, VIOLET, SHADOW_FLOAT } from '../ui/theme.ts';
import { Laurel } from '../ui/Laurel.tsx';

/**
 * variants numbers | profile | plan | reviews.
 *
 * numbers / profile / plan: rows complete on `stepMs`, the fact tile rotates
 * every 3 ticks, and the step auto-advances 150 ms after the last row.
 *
 * reviews: every row has its own bar that fills continuously over `stepMs`
 * (the last row takes 1.8× longer), a check lands when it is full, and the
 * review cards sit underneath. When the step carries a `question`, the bars
 * pause halfway through row `atRow` with a yes/no modal whose answer is stored
 * under `question.answerKey`.
 *
 * Not skippable: there is no CTA and no back target while it runs.
 */
export function Loader(props: StepProps) {
  return props.step.variant === 'reviews' ? <BarsLoader {...props} /> : <TickLoader {...props} />;
}

/* ------------------------------------------------------------------ reviews */

function BarsLoader({ step, content, ctx }: StepProps) {
  const rows = content.rows ?? [];
  const stepMs = step.stepMs ?? 2000;
  const question = step.question && content.question ? { ...step.question, ...content.question } : null;
  // progress in rows: 1.5 = second row half full
  const [progress, setProgress] = useState(0);
  const [asked, setAsked] = useState(false);
  const [modal, setModal] = useState(false);
  const finished = useRef(false);
  const lastDone = useRef(0);

  const durations = rows.map((_, i) => (i === rows.length - 1 && rows.length > 1 ? stepMs * 1.8 : stepMs));

  useEffect(() => {
    ctx.setCta({ label: null });
  }, [step.id]);

  // the bars advance on animation frames, and stop while the modal is up
  useEffect(() => {
    if (modal || progress >= rows.length || !rows.length) return;
    let frame = 0;
    let last = performance.now();
    const tick = () => {
      // the page clock, not the frame timestamp: jsdom and some browsers hand
      // requestAnimationFrame a different origin, which made dt negative
      const now = performance.now();
      const dt = reducedMotion() ? 400 : Math.max(0, now - last);
      last = now;
      setProgress((p) => {
        const i = Math.min(rows.length - 1, Math.floor(p));
        return Math.min(rows.length, p + dt / durations[i]);
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, progress >= rows.length, rows.length, step.id]);

  const done = Math.floor(progress);

  useEffect(() => {
    if (done === lastDone.current) return;
    lastDone.current = done;
    if (done >= rows.length) hapticDone();
    else haptic(10);
  }, [done, rows.length]);

  // the question opens once, halfway through its row
  useEffect(() => {
    if (!question || asked) return;
    if (progress >= question.atRow - 0.5) {
      setAsked(true);
      setModal(true);
    }
  }, [progress, question, asked]);

  // once every bar is full (and no question is open) the step moves on. A ref,
  // not state: a state change here re-ran the effect and its cleanup cancelled
  // the very timer that was about to navigate, which left the screen stuck.
  useEffect(() => {
    if (progress < rows.length || modal || finished.current) return;
    finished.current = true;
    const t = setTimeout(() => (step.autoNext ? ctx.goTo(step.autoNext) : ctx.next()), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, rows.length, modal]);

  const answer = (yes: boolean) => {
    if (!question) return;
    haptic();
    ctx.setAnswer(question.answerKey, { value: yes ? 'yes' : 'no', label: yes ? question.yes : question.no, index: yes ? 0 : 1 });
    setModal(false);
  };

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} aria-live="polite">
        {rows.map((row, i) => {
          const fill = Math.max(0, Math.min(1, progress - i));
          const complete = fill >= 1;
          const active = !complete && i === done;
          return (
            <div key={row.label} style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: fill > 0 || active ? 1 : 0.55, transition: 'opacity .3s' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.25, textAlign: 'left' }}>{row.label}</span>
                {complete ? (
                  <span style={{ width: 24, height: 24, flex: 'none', borderRadius: '50%', background: INK, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', animation: 'fk-pop .4s ease-out' }}>
                    <i className="ph-bold ph-check" style={{ fontSize: 13 }} aria-hidden="true" />
                  </span>
                ) : (
                  <span style={{ fontWeight: 800, fontSize: 15, color: active ? INK : MUTED, fontVariantNumeric: 'tabular-nums', minWidth: 40, textAlign: 'right' }}>{Math.round(fill * 100)}%</span>
                )}
              </div>
              <div style={{ height: 10, borderRadius: 5, background: CREAM, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 5, background: PROGRESS_FILL, width: `${fill * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      <ReviewStack ctx={ctx} proofLine={content.proofLine} />

      {modal && question ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={question.title}
          style={{ position: 'fixed', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'rgba(43,42,51,.45)', animation: 'fk-fill .2s ease-out' }}
        >
          <div style={{ width: '100%', maxWidth: 380, background: PAPER, borderRadius: 22, padding: 22, boxShadow: SHADOW_FLOAT, display: 'flex', flexDirection: 'column', gap: 14, animation: 'fk-pop .4s ease-out' }}>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1.2, textAlign: 'center' }}>{question.title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button type="button" className="fk-cta" onClick={() => answer(true)} style={modalButton(true)}>
                {question.yes}
              </button>
              <button type="button" onClick={() => answer(false)} style={modalButton(false)}>
                {question.no}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

const modalButton = (primary: boolean): React.CSSProperties => ({
  minHeight: 52,
  borderRadius: 999,
  border: primary ? 0 : `2px solid ${LINE}`,
  background: primary ? GRAD : PAPER,
  color: primary ? '#fff' : INK,
  fontWeight: 800,
  fontSize: 16,
  cursor: 'pointer',
  font: 'inherit',
});

const SHOWN = 3;

/** the first three review cards, standing still under the bars */
function ReviewStack({ ctx, proofLine }: { ctx: StepProps['ctx']; proofLine?: string }) {
  const list = ctx.shared.testimonials ?? [];
  if (!list.length) return null;
  const shown = list.slice(0, SHOWN);
  const offset = 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center', marginTop: 4 }}>
      {proofLine ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 800, color: INK, marginBottom: 4, letterSpacing: '-.01em' }}>
          <Laurel />
          <span>{proofLine}</span>
          <Laurel mirrored />
        </div>
      ) : null}
      {shown.map((t) => (
        <div key={`${offset}-${t.name}`} style={{ width: '100%', padding: '14px 16px', borderRadius: 18, background: CREAM, display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left', animation: 'fk-in .3s ease-out' }}>
          <div style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-.01em' }}>{t.result}</div>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: MUTED, lineHeight: 1.45 }}>{t.quote}</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 700, color: MUTED }}>
              {t.flag ? <span aria-hidden="true">{t.flag}</span> : null}
              {t.name}
            </span>
            <span style={{ fontSize: 17, letterSpacing: 2 }} role="img" aria-label="5 stars">
              ⭐⭐⭐⭐⭐
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------- numbers / profile / plan */

function TickLoader({ step, content, ctx }: StepProps) {
  const rows = content.rows ?? [];
  const facts = ctx.shared.facts ?? [];
  const stepMs = step.stepMs ?? 2000;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    ctx.setCta({ label: null });
    const id = setInterval(() => setTick((t) => t + 1), stepMs);
    return () => clearInterval(id);
  }, [step.id, stepMs]);

  const done = Math.min(rows.length, tick);

  useEffect(() => {
    if (done === 0) return;
    if (done >= rows.length) hapticDone();
    else haptic(10);
  }, [done, rows.length]);

  useEffect(() => {
    if (tick <= rows.length) return;
    const t = setTimeout(() => (step.autoNext ? ctx.goTo(step.autoNext) : ctx.next()), 150);
    return () => clearTimeout(t);
  }, [tick, rows.length, step.autoNext]);

  const pct = rows.length ? Math.round((done / rows.length) * 100) : 0;
  const ui = ctx.shared.ui ?? {};
  const eta = done >= rows.length ? (ui.done ?? 'Done') : (ui.secondsLeft ?? '{n}s left').replace('{n}', String(Math.max(1, Math.ceil((rows.length - done) * (stepMs / 1000)))));
  const fact = facts.length ? facts[Math.floor(tick / 3) % facts.length] : '';

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
        {step.variant === 'plan' ? <PlanCard content={content} ui={ui} done={done} total={rows.length} /> : null}

        {step.variant === 'profile' ? (
          <span
            style={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              background: CEL,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: `0 14px 36px ${VIOLET}4d`,
              animation: 'fk-pulse 1.4s ease-out infinite',
            }}
          >
            <Icon name="user-focus" size={44} />
          </span>
        ) : null}

        {step.variant === 'numbers' ? <Ring /> : null}

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 800, color: MUTED }}>
            <span>{pct}%</span>
            <span>{eta}</span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: CREAM, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 4, background: PROGRESS_FILL, width: `${Math.max(4, pct)}%`, transition: 'width .6s cubic-bezier(.2,.8,.2,1)' }} />
          </div>
        </div>

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }} aria-live="polite">
          {rows.map((row, i) => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                opacity: i <= done ? 1 : 0.4,
                padding: '10px 12px',
                borderRadius: 16,
                background: i === done ? CREAM : 'transparent',
                transition: 'all .3s',
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  flex: 'none',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: i < done ? CEL : PAPER,
                  border: `2px solid ${i < done ? 'transparent' : i === done ? ORANGE : LINE}`,
                  color: '#fff',
                  animation: i === done && i < rows.length ? 'fk-pulse 1s ease-out infinite' : 'none',
                  marginTop: 1,
                }}
              >
                <i className="ph-bold ph-check" style={{ fontSize: 13, opacity: i < done ? 1 : 0 }} aria-hidden="true" />
              </span>
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <div style={{ fontWeight: i <= done ? 800 : 600 }}>{row.label}</div>
                {i < done && row.detail ? (
                  <div style={{ fontSize: 13, color: MUTED, fontWeight: 600, animation: 'fk-in .3s ease-out' }}>{row.detail}</div>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {fact && step.variant !== 'plan' ? <FactTile text={fact} /> : null}
      </div>
    </>
  );
}

/** the "plan" variant's assembling card: chips → curve → moves, with a scan line until done. */
function PlanCard({ content, ui, done, total }: { content: StepProps['content']; ui: NonNullable<StepProps['ctx']['shared']['ui']>; done: number; total: number }) {
  const chips = content.plan?.chips ?? [];
  const moves = content.plan?.moves ?? [];
  const chipStyles = [
    { bg: GRAD, fg: '#fff' },
    { bg: INK, fg: '#fff' },
    { bg: PAPER, fg: INK },
  ];
  return (
    <div
      style={{
        flex: 'none',
        position: 'relative',
        width: '100%',
        borderRadius: 26,
        overflow: 'hidden',
        boxShadow: SHADOW_FLOAT,
        background: PAPER,
        border: `2px solid ${LINE}`,
        padding: 14,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 10,
        textAlign: 'left',
      }}
    >
      <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <img src={asset('brand-icon')} alt="" width={18} height={18} style={{ width: 18, height: 18 }} />
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: CORAL_DEEP }}>{ui.yourPlan ?? 'Your plan'}</span>
        </span>
        <span style={{ fontSize: 11, fontWeight: 800, color: MUTED }}>{ui.ninetyDays ?? '30 days'}</span>
      </div>

      <div style={{ gridColumn: '1 / -1', padding: 10, borderRadius: 14, background: CREAM }}>
        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>{ui.aboutYou ?? 'About you'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {chips.map((c, i) => (
            <span key={c} style={{ padding: '4px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: chipStyles[i % 3].bg, color: chipStyles[i % 3].fg, opacity: done >= 1 ? 1 : 0.25, transition: 'all .4s' }}>
              {c}
            </span>
          ))}
        </div>
      </div>

      <div style={{ padding: 10, borderRadius: 14, background: SUN, display: 'flex', flexDirection: 'column', minHeight: 96 }}>
        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: INK, opacity: 0.7 }}>{ui.projectedReach ?? 'Progress'}</div>
        <svg viewBox="0 0 140 60" width="100%" height="56" style={{ display: 'block', marginTop: 'auto' }} aria-hidden="true">
          <path d="M0 54 C 30 52, 55 46, 80 34 S 120 12, 140 5" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeDasharray="200" strokeDashoffset={done >= 2 ? 0 : 200} style={{ transition: 'stroke-dashoffset .8s ease-out' }} />
          <circle cx="140" cy="5" r="4" fill={INK} stroke="#fff" strokeWidth="2" opacity={done >= 2 ? 1 : 0} />
        </svg>
      </div>

      <div style={{ padding: 10, borderRadius: 14, background: CEL, color: '#fff', minHeight: 96 }}>
        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', opacity: 0.85, marginBottom: 4 }}>{ui.firstMoves ?? 'First 3 moves'}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {moves.map((m) => (
            <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, opacity: done >= 3 ? 1 : 0.2, transition: 'opacity .4s' }}>
              <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: VIOLET, flex: 'none' }}>
                <i className="ph-bold ph-check" style={{ fontSize: 9 }} aria-hidden="true" />
              </span>
              {m}
            </div>
          ))}
        </div>
      </div>

      {done < total ? (
        <div style={{ position: 'absolute', left: 0, right: 0, height: 3, background: '#fff', boxShadow: '0 0 14px 5px rgba(255,255,255,.9)', animation: 'fk-scan 2.2s ease-in-out infinite alternate' }} aria-hidden="true" />
      ) : null}
    </div>
  );
}
